import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { VueQueryPlugin, type VueQueryPluginOptions } from '@tanstack/vue-query'
import App from './App.vue'
import router from './router'
import { i18n } from './i18n'

import './styles/fonts.css'
import './styles/tokens.css'
import './styles/base.css'

/**
 * Streama CMS UI · App bootstrap
 *
 * Plugin registration order matters:
 *   1. Pinia       — stores must exist before guards reference them
 *   2. Router      — guards instantiate stores; needs Pinia first
 *   3. i18n        — needed by router-rendered components
 *   4. Vue Query   — depends on nothing else
 *
 * After mount, kick off the auth refresh poller. It's a no-op until
 * the backend's /refresh endpoint lands (see UI_SPRINT1_BACKEND_SPEC.md).
 */

const app = createApp(App)

// Pinia must come first.
const pinia = createPinia()
app.use(pinia)

// Router (uses Pinia in guards).
app.use(router)

// i18n (uses nothing; safe to install anywhere after createApp).
app.use(i18n)

// Vue Query — server state caching.
const vueQueryOptions: VueQueryPluginOptions = {
  queryClientConfig: {
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: 1,
        refetchOnWindowFocus: true,
      },
      mutations: {
        retry: 0,
      },
    },
  },
}
app.use(VueQueryPlugin, vueQueryOptions)

app.mount('#app')

// Token refresh: try at boot (in case we hydrated a near-expired
// token), then poll every 5 minutes. The store handles the no-op
// case when /refresh isn't available.
import('./stores/auth').then(({ useAuthStore }) => {
  const auth = useAuthStore()
  void auth.refreshIfNeeded()
  window.setInterval(() => void auth.refreshIfNeeded(), 5 * 60 * 1000)
})
